(function () {
  const DRAWER_SELECTOR = 'slib-cart-drawer';
  const OPEN_CLASS = 'active';
  const BODY_CLASS = 'slib-cart-drawer-open';

  class SlibCartDrawer extends HTMLElement {
    connectedCallback() {
      this.overlay = this.querySelector('[data-slib-cart-drawer-close]');
      this.panel = this.querySelector('.slib-cart-drawer__inner');
      this.content = this.querySelector('[id^="SlibCartDrawerContent-"]');
      this.boundDocumentClick = this.handleDocumentClick.bind(this);
      this.boundKeydown = this.handleKeydown.bind(this);
      this.boundChangeClick = this.handleChangeClick.bind(this);
      this.boundQuantityInput = this.handleQuantityInput.bind(this);
      this.boundProductAdded = this.handleProductAdded.bind(this);

      document.addEventListener('click', this.boundDocumentClick);
      document.addEventListener('keydown', this.boundKeydown);
      document.addEventListener('product-form:added', this.boundProductAdded);
      this.addEventListener('click', this.boundChangeClick);
      this.addEventListener('change', this.boundQuantityInput);
      this.startCountdowns();

      if (this.classList.contains(OPEN_CLASS)) {
        document.body.classList.add(BODY_CLASS);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.boundDocumentClick);
      document.removeEventListener('keydown', this.boundKeydown);
      document.removeEventListener('product-form:added', this.boundProductAdded);
      this.clearCountdowns();
    }

    handleDocumentClick(event) {
      const closeButton = event.target.closest('[data-slib-cart-drawer-close]');
      if (closeButton && this.contains(closeButton)) {
        event.preventDefault();
        this.close();
        return;
      }

      const opener = event.target.closest('[data-slib-cart-drawer-open], a[href]');
      if (!opener || opener.closest(DRAWER_SELECTOR) || opener.hasAttribute('data-slib-cart-drawer-ignore')) return;
      const isExplicitOpener = opener.hasAttribute('data-slib-cart-drawer-open');
      const isCartLink = this.isCartLink(opener);
      if (!isExplicitOpener && !isCartLink) return;
      if (!isExplicitOpener && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || opener.target === '_blank')) return;

      event.preventDefault();
      this.open(opener);
    }

    handleKeydown(event) {
      if (event.key === 'Escape' && this.classList.contains(OPEN_CLASS)) {
        this.close();
      }
    }

    handleChangeClick(event) {
      const quantityButton = event.target.closest('[data-slib-cart-quantity-button]');
      if (quantityButton) {
        const wrap = quantityButton.closest('.slib-cart-quantity');
        const input = wrap && wrap.querySelector('[data-slib-cart-quantity-input]');
        if (!input) return;
        if (input.dataset.slibCartBundleLine === 'true') {
          if (Number(quantityButton.dataset.step || 0) > 0) {
            this.duplicateBundleLine(input.dataset.index);
          } else {
            this.changeLine(input.dataset.index, 0);
          }
          return;
        }
        const nextQuantity = Math.max(0, Number(input.value || 0) + Number(quantityButton.dataset.step || 0));
        input.value = nextQuantity;
        this.changeLine(input.dataset.index, nextQuantity);
        return;
      }

      const changeButton = event.target.closest('[data-slib-cart-change]');
      if (changeButton) {
        this.changeLine(changeButton.dataset.index, Number(changeButton.dataset.quantity || 0));
        return;
      }

      const discountButton = event.target.closest('[data-slib-cart-discount-apply]');
      if (discountButton) {
        this.applyDiscount(discountButton);
      }
    }

    handleQuantityInput(event) {
      const variantSelect = event.target.closest('[data-slib-cart-line-variant-select]');
      if (variantSelect) {
        this.changeLineVariant(variantSelect.dataset.index, variantSelect.value);
        return;
      }

      const input = event.target.closest('[data-slib-cart-quantity-input]');
      if (!input) return;
      if (input.dataset.slibCartBundleLine === 'true') return;
      this.changeLine(input.dataset.index, Math.max(0, Number(input.value || 0)));
    }

    async handleProductAdded(event) {
      this.classList.add('is-updating');
      this.setAttribute('aria-busy', 'true');

      try {
        await this.refresh();
        this.open(event.target);
      } catch (error) {
        console.error('Cart drawer refresh failed after add', error);
      } finally {
        this.classList.remove('is-updating');
        this.removeAttribute('aria-busy');
      }
    }

    isCartLink(link) {
      try {
        const url = new URL(link.href, window.location.origin);
        return url.origin === window.location.origin && url.pathname.replace(/\/$/, '') === '/cart';
      } catch (error) {
        return false;
      }
    }

    open(trigger) {
      this.activeElement = trigger || document.activeElement;
      this.classList.add(OPEN_CLASS);
      document.body.classList.add(BODY_CLASS);
      this.setAttribute('open', '');
      this.startCountdowns();
      window.setTimeout(() => {
        const focusTarget = this.querySelector('.slib-cart-drawer__close') || this.panel;
        if (focusTarget) focusTarget.focus();
      }, 50);
    }

    close() {
      this.classList.remove(OPEN_CLASS);
      this.removeAttribute('open');
      document.body.classList.remove(BODY_CLASS);
      if (this.activeElement && typeof this.activeElement.focus === 'function') {
        this.activeElement.focus();
      }
    }

    async changeLine(index, quantity) {
      if (!index) return;
      this.classList.add('is-updating');
      this.setAttribute('aria-busy', 'true');

      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            line: Number(index),
            quantity: Number(quantity)
          })
        });
        await this.refresh();
      } catch (error) {
        console.error('Cart drawer update failed', error);
      } finally {
        this.classList.remove('is-updating');
        this.removeAttribute('aria-busy');
      }
    }

    async changeLineVariant(index, variantId) {
      if (!index || !variantId) return;
      this.classList.add('is-updating');
      this.setAttribute('aria-busy', 'true');

      try {
        const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        const cart = await cartResponse.json();
        const item = cart.items[Number(index) - 1];
        const properties = item && item.properties ? item.properties : {};

        await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            line: Number(index),
            id: Number(variantId),
            quantity: item ? Number(item.quantity || 1) : 1,
            properties
          })
        });
        await this.refresh();
      } catch (error) {
        console.error('Cart drawer variant update failed', error);
      } finally {
        this.classList.remove('is-updating');
        this.removeAttribute('aria-busy');
      }
    }

    async duplicateBundleLine(index) {
      if (!index) return;
      this.classList.add('is-updating');
      this.setAttribute('aria-busy', 'true');

      try {
        const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        const cart = await cartResponse.json();
        const item = cart.items[Number(index) - 1];
        if (!item) return;

        const properties = { ...(item.properties || {}) };
        const bundleId = properties._bundle_id || `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        const slots = cart.items
          .filter((cartItem) => cartItem.properties && cartItem.properties._bundle_id === bundleId)
          .map((cartItem) => cartItem.properties && cartItem.properties._bundle_slot)
          .map((slot) => Number(slot || 0))
          .filter((slot) => slot > 0);
        properties._bundle_id = bundleId;
        properties._bundle_slot = String((slots.length ? Math.max(...slots) : 0) + 1);

        await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: [
              {
                id: item.variant_id,
                quantity: 1,
                properties
              }
            ]
          })
        });
        await this.refresh();
      } catch (error) {
        console.error('Cart drawer bundle duplicate failed', error);
      } finally {
        this.classList.remove('is-updating');
        this.removeAttribute('aria-busy');
      }
    }

    async refresh() {
      const sectionId = this.dataset.sectionId;
      if (!sectionId) return;

      const response = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const freshDrawer = doc.querySelector(DRAWER_SELECTOR);
      const freshContent = freshDrawer && freshDrawer.querySelector('[id^="SlibCartDrawerContent-"]');
      if (!freshContent || !this.content) return;

      this.content.innerHTML = freshContent.innerHTML;
      this.classList.toggle('is-empty', freshDrawer.classList.contains('is-empty'));
      this.startCountdowns();
      this.updateHeaderCartCount();
    }

    applyDiscount(button) {
      const container = button.closest('.slib-cart-discount');
      const input = container && container.querySelector('[data-slib-cart-discount-input]');
      const error = container && container.querySelector('[data-slib-cart-discount-error]');
      const code = input && input.value.trim();

      if (!code) {
        if (error) error.hidden = false;
        return;
      }

      window.location.href = `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(window.location.pathname)}`;
    }

    async updateHeaderCartCount() {
      try {
        const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        const cart = await response.json();
        document.querySelectorAll('.slib-header__cart-count').forEach((node) => {
          node.textContent = cart.item_count;
          node.hidden = cart.item_count < 1;
        });
      } catch (error) {
        console.error('Cart count update failed', error);
      }
    }

    startCountdowns() {
      this.clearCountdowns();
      this.countdownIntervals = [];

      this.querySelectorAll('[data-slib-cart-countdown]').forEach((node) => {
        const duration = Number(node.dataset.duration || 300);
        const started = Date.now();
        const tick = () => {
          const elapsed = Math.floor((Date.now() - started) / 1000);
          const remaining = Math.max(0, duration - elapsed);
          const minutes = String(Math.floor(remaining / 60)).padStart(2, '0');
          const seconds = String(remaining % 60).padStart(2, '0');
          node.textContent = `${minutes}:${seconds}`;
        };
        tick();
        this.countdownIntervals.push(window.setInterval(tick, 1000));
      });
    }

    clearCountdowns() {
      if (!this.countdownIntervals) return;
      this.countdownIntervals.forEach((interval) => window.clearInterval(interval));
      this.countdownIntervals = [];
    }
  }

  if (!customElements.get('slib-cart-drawer')) {
    customElements.define('slib-cart-drawer', SlibCartDrawer);
  }
})();
